/**
 * Dynamic Exception Route Component
 *
 * Pure orchestrator: looks up the per-code config in the registry,
 * runs the URL state + fetch + filter-strip pipeline, and hands the
 * results to the shared exception data table. Per-exception behavior
 * (filter strips, server pagination, custom headers, legacy
 * redirects, count fetchers) lives on the `ExceptionConfig` registry,
 * not in this file.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Helmet } from 'react-helmet-async'

import { DataViewTable } from '../../../components/shared/DataViewTable'
import { allowIgnore } from '../../../types/exceptions'
import ExceptionPageHeader, {
  resolvePageHeader,
} from '../../../components/inspector/shared/ExceptionPageHeader'
import FpVisibilityIcon from '../../../components/inspector/shared/FpVisibilityIcon'
import ExceptionStatusToggle from '../../../components/inspector/shared/ExceptionStatusToggle'
import OldestOpenChip from '../../../components/inspector/shared/OldestOpenChip'
import { readExceptionStatus } from '../../../components/inspector/shared/exceptionStatus'
import DataViewFilterPanel from '../../../components/inspector/shared/DataViewFilterPanel'
import { getExceptionConfig } from '../../../config/exceptions/registry'
import { useInspector } from '../../../contexts/InspectorContext'
import { useExceptionDataFetch } from '../../../hooks/useExceptionDataFetch'
import { useExceptionUrlState } from '../../../hooks/useExceptionUrlState'
import { useExceptionFilterValues } from '../../../hooks/useExceptionFilterValues'
import { useExceptionTypeFetch } from '../../../hooks/useExceptionTypeFetch'
import { useDrawerDeepLink } from '../../../hooks/useDrawerDeepLink'
import { useSiblingStrip } from '../../../hooks/useSiblingStrip'
import ExceptionDrawer from '../../../components/inspector/shared/ExceptionDrawer'
import { ExceptionObjectType, generateExceptionUrl, generateInspectorUrl } from '../../../utils/urls'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import { buildActiveFilterChips } from '../../../components/inspector/shared/buildActiveFilterChips'
import type {
  ExceptionFilterStripDef,
  ExceptionRowIdentity,
} from '../../../config/exceptions/types'

const DOMAIN_KEY_MAP: Record<string, ExceptionObjectType> = {
  sale: 'sales',
  stock_management: 'stock-management',
  product_setup: 'product-setup',
  product_pricing: 'product-pricing',
  system: 'systems',
  client: 'client',
  accounting: 'accounting',
}

const DynamicExceptionRoute: React.FC = () => {
  const { exception_code } = useParams<{
    exception_code: string
  }>()
  // Bare code for data APIs; tenureId pins the within-FP links to the
  // current tenure (so viewing a past tenure doesn't jump to the active one).
  const { fpCode: fp_code, tenureId } = useInspectorRef()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { franchiseData, setDynamicCrumbOverride } = useInspector()

  const config = exception_code ? getExceptionConfig(exception_code) : null

  // Whether this code is a legacy alias awaiting a redirect. Computed
  // up-front so the data fetch can skip the now-stale code.
  const pendingRedirect = useMemo(
    () => (exception_code ? config?.legacyRedirects?.[exception_code] ?? null : null),
    [config, exception_code],
  )

  // ── Legacy code redirect ─────────────────────────────────────────
  // Hitting one of the discrete sales-compare codes (or any future
  // legacy alias declared in a config) lands the user on the canonical
  // code with the corresponding URL param preset. Done before the data
  // fetch so the deprecated code doesn't trigger a needless backend
  // call. The current URL slug (e.g. `sales`) carries over - redirects
  // are always within the same domain.
  useEffect(() => {
    if (!fp_code || !pendingRedirect) return
    const segments = location.pathname.split('/')
    if (segments.length < 6) return
    segments[5] = pendingRedirect.to
    const target = `${segments.join('/')}?${pendingRedirect.paramKey}=${pendingRedirect.paramValue}`
    navigate(target, { replace: true })
  }, [fp_code, pendingRedirect, location.pathname, navigate])

  // ── Exception type fetch (for header / breadcrumb / domain) ──────
  const { exceptionType, typeLoading, typeError } = useExceptionTypeFetch({
    fpCode: fp_code,
    exceptionCode: exception_code,
    pendingRedirect,
  })

  // ── URL state (server pagination + search) + data fetch ──────────
  const {
    urlState,
    searchInput,
    setSearchInput,
    setPage,
    setPageSize,
    serverTableEnabled,
    searchEnabled,
    exceptionObject,
    setExceptionObject,
  } = useExceptionUrlState({ config, searchParams, setSearchParams })

  // ── Lifted filter-values state ───────────────────────────────────
  // Lifted out of `DataViewTable` so both the panel (renders the
  // controls), the table (applies the predicates) and `buildFetchParams`
  // (translates panel values into server-side query params, e.g. the
  // shared sales filters: sale-date range, LS status, has-Xero-credit)
  // read one source of truth. `useExceptionFilterValues` resets when
  // `config.filters` changes (e.g. swapping to a different exception
  // code).
  const {
    filterValues,
    updateFilterValue,
    filterEnabled,
    setFilterEnabled,
    resetFilters,
    hasAnyActiveFilter,
  } = useExceptionFilterValues(config)

  const { data, response, loading, error: fetchError, refetch } = useExceptionDataFetch({
    fpCode: fp_code,
    exceptionCode: exception_code,
    dataDomain: exceptionType?.data_domain,
    config,
    urlState,
    filterValues,
    skip: pendingRedirect != null || !exceptionType,
  })

  // ── Deep-link drawer: ?exceptionObject=<object_id> resolves a row ─
  // Orchestration (on-page fast path, central re-resolve, row swap,
  // note-count bridge, unresolvable alert) lives in `useDrawerDeepLink`.
  const {
    drawerRow,
    drawerRowLoading,
    localRow,
    unresolvableAlert,
    setUnresolvableAlert,
    noteCountInvalidateRef,
  } = useDrawerDeepLink({
    fpCode: fp_code,
    exceptionCode: exception_code,
    dataDomain: exceptionType?.data_domain,
    data,
    exceptionObject,
    setExceptionObject,
  })

  // ── Optional filter-counts fetch (e.g. sales-compare mismatch) ───
  const [filterCounts, setFilterCounts] = useState<Record<string, number> | null>(null)
  useEffect(() => {
    if (!fp_code || !config?.fetchFilterCounts) {
      setFilterCounts(null)
      return
    }
    let cancelled = false
    config
      .fetchFilterCounts(fp_code)
      .then((res) => {
        if (!cancelled) setFilterCounts(res?.counts ?? null)
      })
      .catch((err: any) => {
        console.error('Failed to fetch filter counts:', err)
      })
    return () => {
      cancelled = true
    }
  }, [fp_code, config])

  // ── Lifted summary-chip state ────────────────────────────────────
  // Hoisted from DataViewTable so DataViewFilterPanel can render
  // the chip cards in its top zone while filtering still happens in the
  // table. Resets when the active exception code changes - state is
  // exception-scoped, not route-scoped.
  const [activeChipKey, setActiveChipKey] = useState<string | null>(null)
  // Parallel multi-select state. Used only when ``config.summaryChips.multiSelect``
  // is true. Initial value comes from the exception's declared
  // ``defaultActiveKeys`` so the table can land on an actionable subset
  // (e.g. Missing+Partial) on first load.
  const chipMultiSelect = !!config?.summaryChips?.multiSelect
  const [activeChipKeys, setActiveChipKeys] = useState<string[]>(
    () => [...(config?.summaryChips?.defaultActiveKeys ?? [])],
  )
  useEffect(() => {
    setActiveChipKey(null)
    setActiveChipKeys([...(config?.summaryChips?.defaultActiveKeys ?? [])])
  }, [exception_code, config])


  // ── Sibling-group strip (auto-derived from siblingCodes.ts) ──────
  const { siblingStrip } = useSiblingStrip({
    fpCode: fp_code,
    tenureId,
    exceptionCode: exception_code,
    config,
  })

  // ── Breadcrumb override ──────────────────────────────────────────
  useEffect(() => {
    if (!exceptionType) return
    const crumb =
      config?.pageHeader?.breadcrumb ?? exceptionType.name_short
    if (crumb) setDynamicCrumbOverride(crumb)
    return () => setDynamicCrumbOverride(null)
  }, [exceptionType, config, setDynamicCrumbOverride])

  // ── Closed-view affordances ──────────────────────────────────────
  // The page-header pill toggles `?status=closed`. When closed, the
  // table body fades via `isClosedView` below; the "last 30 days"
  // helper that used to ride here as a filter-panel caption now lives
  // beside the Open/Closed control in the sticky Results toolbar
  // (ExceptionStatusToggle), so the filter card no longer auto-opens
  // just to display that one note. The config's own ``panelMetadata``
  // (if any) flows through unchanged.
  const isClosed = readExceptionStatus(searchParams) === 'closed'
  const wrappedPanelMetadata = config?.panelMetadata

  // ── Loading / error / unknown-config guards ──────────────────────
  if (typeLoading || pendingRedirect) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading exception type...
          </Typography>
        </Stack>
      </Box>
    )
  }

  const handleNavigateBack = () => {
    const slug = DOMAIN_KEY_MAP[exceptionType?.data_domain ?? '']
    if (slug && fp_code) navigate(generateExceptionUrl(fp_code, slug, undefined, tenureId))
    else navigate(generateInspectorUrl(fp_code, { id: tenureId }))
  }

  if (typeError || !exceptionType) {
    return (
      <Box p={3}>
        <Alert severity="error">{typeError || 'Exception type not found'}</Alert>
        <Box mt={2}>
          <Button variant="outlined" onClick={handleNavigateBack}>
            Back to Exceptions
          </Button>
        </Box>
      </Box>
    )
  }

  if (!config) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No component found for exception type "{exception_code}".
        </Alert>
      </Box>
    )
  }

  // ── Render ───────────────────────────────────────────────────────
  const franchiseName = franchiseData?.franchise_name || fp_code || 'Loading'
  const header = resolvePageHeader(config, exceptionType)
  const breadcrumbName = config.pageHeader?.breadcrumb ?? exceptionType.name_short
  const pageTitle = `${breadcrumbName} | ${franchiseName} | Inspector Tool`

  const isEmpty = !loading && !fetchError && data.length === 0
  const emptyMessage = (() => {
    if (typeof config.emptyMessage === 'function') {
      return config.emptyMessage(urlState, exceptionType)
    }
    if (typeof config.emptyMessage === 'string') return config.emptyMessage
    // Neutral default - phrasing the empty state around "the underlying
    // entity" (sales / invoices / products) reads as "this FP has none
    // of those", which is misleading for a slice that filters down to a
    // specific exception type. "No exceptions found" frames the slice
    // as "no issues match", which is what we actually mean.
    return 'No exceptions found'
  })()

  // Sibling-group strip (when present) renders before any
  // config-declared strips so navigation always sits above filters.
  const strips: ExceptionFilterStripDef[] = []
  if (siblingStrip) strips.push(siblingStrip)
  if (config.filterStrips) strips.push(...config.filterStrips)

  // Summary chips: lift state into the panel when the config declares
  // chip categories and the response carries the counts block.
  const chipCountsFromResponse = (config.summaryChips && response
    ? (response[config.summaryChips.countsKey] as Record<string, number> | undefined)
    : undefined) ?? null
  const hasSummaryChips = !!config.summaryChips && !!chipCountsFromResponse

  // ── Active filter chips (the toolbar's `Showing:` breadcrumb) ────
  const activeFilterChips = buildActiveFilterChips({
    strips,
    config,
    response,
    rows: data,
    filterCounts,
    searchParams,
    setSearchParams,
    filterValues,
    updateFilterValue,
    filterEnabled,
    setFilterEnabled,
    summaryChips: hasSummaryChips ? config.summaryChips : undefined,
    activeChipKey,
    setActiveChipKey: (key) => setActiveChipKey(key),
    activeChipKeys,
    setActiveChipKeys,
    serverSearch: searchEnabled
      ? { value: searchInput, onChange: setSearchInput }
      : undefined,
  })

  // ── Reset filters ────────────────────────────────────────────────
  // Clears generic filter values, the summary-chip key, and every URL
  // param except `status` (the Open/Closed toggle lives in the page
  // header and is intentionally preserved).
  const handleResetFilters = () => {
    resetFilters()
    setActiveChipKey(null)
    setActiveChipKeys([...(config?.summaryChips?.defaultActiveKeys ?? [])])
    if (searchEnabled) setSearchInput('')
    const next = new URLSearchParams()
    const status = searchParams.get('status')
    if (status) next.set('status', status)
    setSearchParams(next, { replace: false })
  }
  const defaultActiveKeys = config?.summaryChips?.defaultActiveKeys ?? []
  const chipKeysDiffer =
    activeChipKeys.length !== defaultActiveKeys.length ||
    activeChipKeys.some((k) => !defaultActiveKeys.includes(k))
  const hasAnyActiveSelection =
    hasAnyActiveFilter ||
    activeChipKey != null ||
    (chipMultiSelect && chipKeysDiffer) ||
    (searchEnabled && !!searchInput) ||
    activeFilterChips.length > 0

  // Empty-state node rendered inside the table card when the loaded
  // page has zero rows. Builds a calm "nothing matched" surface (icon
  // + heading + subtext) instead of an outlined alert. The route owns
  // this UI because the exception-type wording lives here - the table
  // is exception-agnostic.
  const defaultEmptyStateNode = (
    <Stack
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 8,
        px: 3,
        width: '100%',
        textAlign: 'center',
      }}
    >
      <SearchOffIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          color: 'text.primary',
          whiteSpace: 'nowrap',
        }}
      >
        {emptyMessage}
      </Typography>
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
        Try clearing the filters or change the exception status.
      </Typography>
    </Stack>
  )
  const customEmptyState = config.renderEmptyState
    ? config.renderEmptyState(response, urlState)
    : null
  const emptyStateNode = customEmptyState ?? defaultEmptyStateNode

  return (
    <Box>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <ExceptionPageHeader
        domain={exceptionType.data_domain}
        title={header.title}
        description={header.description}
        exceptionType={exceptionType.exception_type}
        titleAdornment={
          <FpVisibilityIcon
            kind="exception"
            visibleToRoles={exceptionType.visible_to_roles}
            variant="chip"
            size="large"
          />
        }
      />
      <DataViewFilterPanel
        config={config}
        strips={strips}
        response={response}
        rows={data}
        filterCounts={filterCounts}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        panelMetadata={wrappedPanelMetadata}
        summaryChips={hasSummaryChips ? config.summaryChips : undefined}
        summaryChipsControl={
          hasSummaryChips
            ? chipMultiSelect
              ? {
                  activeKey: null,
                  activeKeys: activeChipKeys,
                  onChipClick: (key) => {
                    const isActive = activeChipKeys.includes(key)
                    setActiveChipKeys(
                      isActive
                        ? activeChipKeys.filter((k) => k !== key)
                        : [...activeChipKeys, key],
                    )
                  },
                  counts: chipCountsFromResponse ?? {},
                }
              : {
                  activeKey: activeChipKey,
                  onChipClick: (key) =>
                    setActiveChipKey(activeChipKey === key ? null : key),
                  counts: chipCountsFromResponse ?? {},
                }
            : undefined
        }
        filterValues={filterValues}
        updateFilterValue={updateFilterValue}
        filterEnabled={filterEnabled}
        setFilterEnabled={setFilterEnabled}
        onReset={handleResetFilters}
        hasAnyActiveFilter={hasAnyActiveSelection}
        serverSearch={
          searchEnabled
            ? { value: searchInput, onChange: setSearchInput }
            : undefined
        }
      />
      {/* Deep-link drawer alerts sit between the filter card and the
       * results so a missing-row message reads alongside the filter
       * context that produced it, not buried at the bottom of the
       * page. The in-flight state shows the drawer's own skeleton (see
       * the drawer mount below), not a banner here. */}
      {unresolvableAlert && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setUnresolvableAlert(null)}
        >
          {unresolvableAlert}
        </Alert>
      )}
      {!isEmpty && !fetchError && config.renderHeaderSummary && (
        <Box mb={2}>{config.renderHeaderSummary(data, response)}</Box>
      )}
      {fetchError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      ) : (
        <DataViewTable
          config={config}
          data={data}
          response={response || undefined}
          variant="inspector"
          fpCode={fp_code!}
          isLoading={loading}
          isClosedView={isClosed}
          emptyMessage={emptyStateNode}
          exceptionTypeName={exceptionType?.name_short ?? exceptionType?.name}
          exceptionType={exceptionType?.exception_type}
          allowIgnore={allowIgnore(exceptionType)}
          statusToggle={
            // The pill is part of the sticky Results toolbar, not the
            // filter card. Pre-checking the envelope keys here keeps the
            // toolbar layout stable on live-classifier views where a
            // Closed slice isn't serveable (envelope omits open_total /
            // fixed_total_30d so the toggle has no data to render).
            typeof response?.open_total === 'number'
            && typeof response?.fixed_total_30d === 'number' ? (
              <ExceptionStatusToggle
                response={response}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            ) : undefined
          }
          oldestOpenChip={
            <OldestOpenChip
              response={response}
              searchParams={searchParams}
            />
          }
          serverPagination={
            serverTableEnabled && typeof response?.total === 'number'
              ? {
                  page: urlState.page,
                  pageSize: urlState.pageSize,
                  total: response.total,
                  onPageChange: setPage,
                  onPageSizeChange: setPageSize,
                  rowsPerPageOptions: config.serverTable?.pageSizeOptions ?? [50],
                }
              : undefined
          }
          serverSearch={
            searchEnabled
              ? { value: searchInput, onChange: setSearchInput }
              : undefined
          }
          summaryChipsControl={
            hasSummaryChips
              ? chipMultiSelect
                ? {
                    activeKey: null,
                    setActiveKey: () => {},
                    activeKeys: activeChipKeys,
                    setActiveKeys: setActiveChipKeys,
                  }
                : { activeKey: activeChipKey, setActiveKey: setActiveChipKey }
              : undefined
          }
          filterValues={filterValues}
          updateFilterValue={updateFilterValue}
          filterEnabled={filterEnabled}
          activeFilterChips={activeFilterChips}
          title={header.title}
          onAfterMutation={refetch}
          controlledExceptionObject={exceptionObject}
          onRowOpen={(row) =>
            setExceptionObject(
              row
                ? String(
                    (row as ExceptionRowIdentity).object_id ?? '',
                  ) || null
                : null,
            )
          }
          onNoteCountsReady={(invalidate) => {
            noteCountInvalidateRef.current = invalidate
          }}
        />
      )}
      {/* Drawer mounted at the route so the deep-link path
       * (?exceptionObject=<object_id>) can resolve a row that isn't in
       * the current page of results via the central drawer-detail
       * endpoint (`useExceptionDrawerDetail`). It opens immediately and
       * shows the drawer's skeleton while resolving (admin-host parity);
       * a not-found alert renders above the results. Click paths feed
       * the same drawer because the table delegates opens via
       * `onRowOpen`. */}
      {fp_code && (exceptionObject || drawerRow) && (
        <ExceptionDrawer
          open={!!exceptionObject}
          loading={localRow === null ? drawerRowLoading : false}
          onClose={() => setExceptionObject(null)}
          row={drawerRow}
          fpCode={fp_code}
          // Compare deep-link ownership: the page table owns it when its config
          // ships a compare-sales rowDetail (the main sales exceptions do), so the
          // drawer must yield there - otherwise both bridges auto-open the modal
          // twice. The drawer owns it only on exception pages without that rowDetail.
          compareDeepLink={config?.rowDetail?.type !== 'compare-sales'}
          // This IS a dedicated exception view - the page's code is the
          // current-exception bias, so the drawer floats that problem to the
          // top and auto-expands it (Data Explorer / admin pass no focus).
          focusCode={exception_code}
          exceptionTypeName={exceptionType?.name_short ?? exceptionType?.name}
          exceptionType={exceptionType?.exception_type}
          allowIgnore={allowIgnore(exceptionType)}
          getTitle={config?.getRowTitle}
          getRowTitle={config?.getRowTitle}
          getRowSubtitle={config?.getRowSubtitle}
          franchiseData={franchiseData}
          onAfterMutation={refetch}
          // Mirror what the table's internal drawer does in uncontrolled
          // mode: bump the per-row count so the Issues-chip note dot
          // appears immediately after the first note is posted and
          // disappears after the last is deleted, without a full data
          // refetch. ``noteCountInvalidateRef`` is populated by the
          // table on mount via ``onNoteCountsReady``.
          onCountDelta={(delta) => {
            if (!drawerRow) return
            noteCountInvalidateRef.current?.(
              {
                exception_code: drawerRow.exception_code,
                data_domain: drawerRow.data_domain,
                object_id: String(drawerRow.object_id),
              },
              delta,
            )
          }}
        />
      )}
    </Box>
  )
}

export default DynamicExceptionRoute
